import type { StoreProductResult } from "../services/ProductSearchService";

export const formatTimestamp = (ts: number, now = Date.now()): string => {
  const diffMs = now - ts;
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(ts).toLocaleDateString();
};

export const sortResultsByInventory = (
  results: StoreProductResult[]
): StoreProductResult[] => {
  return [...results].sort((a, b) => {
    if (a.error && !b.error) return 1;
    if (!a.error && b.error) return -1;
    if (a.error && b.error) return 0;

    const aInStock =
      a.status === "IN_STOCK" || (a.inventory !== undefined && a.inventory > 0);
    const bInStock =
      b.status === "IN_STOCK" || (b.inventory !== undefined && b.inventory > 0);
    if (aInStock && !bInStock) return -1;
    if (!aInStock && bInStock) return 1;

    const aHasPrice = a.storePrice && a.storePrice > 0;
    const bHasPrice = b.storePrice && b.storePrice > 0;
    if (aHasPrice && bHasPrice) return a.storePrice - b.storePrice;
    if (aHasPrice && !bHasPrice) return -1;
    if (!aHasPrice && bHasPrice) return 1;

    return (
      (b.inventory || 0) +
      (b.backroom || 0) -
      (a.inventory || 0) -
      (a.backroom || 0)
    );
  });
};

export const formatSearchErrorMessage = (message: string): string => {
  if (message.toLowerCase().includes("item not found")) {
    return "Item not found. Please check your UPC or SKU and try again.";
  }
  return message;
};

export type SearchType = "UPC" | "SKU" | "URL" | "QR";
export type DetectedCodeType = SearchType | "Unknown";

const PRODUCT_LINK_HOSTS = ["walmart.com", "w-mt.co", "homedepot.com", "tractorsupply.com"];

// Share sheets (Walmart's "What do you think of this?") paste extra words around
// the product link. Pull out just the link so the search box parses it.
export const extractProductLink = (input: string): string => {
  const trimmed = input.trim();
  if (!/\s/.test(trimmed)) return input;
  const tokens = trimmed.split(/\s+/).map((token) => token.replace(/^[<("']+|[>)"'.,!?;:]+$/g, ""));
  const link = tokens.find((token) => PRODUCT_LINK_HOSTS.some((host) => token.toLowerCase().includes(host)));
  return link || input;
};

export const identifyCodeType = (
  inputCode: string,
  retailer: string
): DetectedCodeType => {
  if (!inputCode || inputCode.trim().length === 0) return "Unknown";

  const trimmedCode = inputCode.trim();

  if (
    trimmedCode.includes("walmart.com") ||
    trimmedCode.includes("w-mt.co") ||
    trimmedCode.includes("homedepot.com") ||
    trimmedCode.includes("tractorsupply.com")
  ) {
    return trimmedCode.includes("w-mt.co/q/") ? "QR" : "URL";
  }

  if (retailer === "target" || retailer === "bestbuy" || retailer === "costco" || retailer === "tractor_supply") {
    return /^\d+$/.test(trimmedCode) ? "SKU" : "Unknown";
  }

  if (trimmedCode.length === 12 && /^\d+$/.test(trimmedCode)) {
    return "UPC";
  }

  if (/^[A-Za-z0-9-]+$/.test(trimmedCode)) {
    return "SKU";
  }

  return "Unknown";
};

export const parseSkuFromUrl = (
  url: string,
  messages: {
    invalidUrlFormat: string;
    invalidSkuFormat: string;
    urlParseError: string;
    failedToParseUrl: string;
  }
): string => {
  try {
    let normalizedUrl = url.trim();
    if (
      !normalizedUrl.startsWith("http://") &&
      !normalizedUrl.startsWith("https://")
    ) {
      normalizedUrl = "https://" + normalizedUrl;
    }

    const urlObj = new URL(normalizedUrl);

    if (urlObj.hostname.includes("walmart.com")) {
      const pathParts = urlObj.pathname.split("/");
      const ipIndex = pathParts.findIndex((part) => part === "ip");

      if (ipIndex === -1 || ipIndex + 1 >= pathParts.length) {
        throw new Error(messages.invalidUrlFormat);
      }

      const firstPart = pathParts[ipIndex + 1];
      let sku: string;

      if (/^\d+$/.test(firstPart)) {
        sku = firstPart;
      } else {
        if (ipIndex + 2 >= pathParts.length) {
          throw new Error(messages.invalidUrlFormat);
        }
        sku = pathParts[ipIndex + 2];
      }

      if (!/^\d+$/.test(sku)) {
        throw new Error(messages.invalidSkuFormat);
      }

      return sku;
    }

    if (urlObj.hostname.includes("homedepot.com")) {
      const pathParts = urlObj.pathname.split("/");
      const pIndex = pathParts.findIndex((part) => part === "p");

      if (pIndex === -1 || pIndex + 2 >= pathParts.length) {
        throw new Error(messages.invalidUrlFormat);
      }

      const productId = pathParts[pIndex + 2];
      if (!/^\d+$/.test(productId)) {
        throw new Error(messages.invalidSkuFormat);
      }

      return productId;
    }

    if (urlObj.hostname.includes("tractorsupply.com")) {
      const lastPart = urlObj.pathname.split("/").filter(Boolean).pop() || "";
      const match = lastPart.match(/(?:^|-)(\d+)$/);
      if (!match) {
        throw new Error(messages.invalidSkuFormat);
      }
      return match[1];
    }

    throw new Error(messages.urlParseError);
  } catch (error: unknown) {
    const message = error instanceof Error ? error.message : "Unknown error";
    throw new Error(`${messages.failedToParseUrl} ${message}`);
  }
};

export const getStoredZipCode = (storage: Storage): string => {
  try {
    return storage.getItem("dealhawk-zip-code") || "";
  } catch {
    return "";
  }
};

export const saveZipCode = (storage: Storage, zipCode: string): void => {
  try {
    storage.setItem("dealhawk-zip-code", zipCode);
  } catch {}
};

export type RecentSearch = {
  id: string;
  searchTerm: string;
  searchType: "UPC" | "SKU" | "URL" | "QR";
  productName?: string;
  productImage?: string;
  timestamp: number;
};

export const migrateOldRecentSearches = (
  storage: Storage,
  retailer: string
) => {
  try {
    const oldKey = "dealhawk-recent-searches";
    const oldSearches = storage.getItem(oldKey);
    if (oldSearches) {
      const newKey = `dealhawk-recent-searches-${retailer}`;
      if (!storage.getItem(newKey)) {
        storage.setItem(newKey, oldSearches);
      }
      storage.removeItem(oldKey);
    }
  } catch {}
};

export const getRecentSearches = (
  storage: Storage,
  retailer: string
): RecentSearch[] => {
  try {
    migrateOldRecentSearches(storage, retailer);
    const key = `dealhawk-recent-searches-${retailer}`;
    const stored = storage.getItem(key);
    return stored ? JSON.parse(stored) : [];
  } catch {
    return [];
  }
};

export const saveRecentSearch = (input: {
  storage: Storage;
  retailer: string;
  search: Omit<RecentSearch, "id" | "timestamp">;
  now?: number;
  limit?: number;
}) => {
  try {
    const existing = getRecentSearches(input.storage, input.retailer);
    const isDuplicate = existing.find(
      (s) =>
        s.searchTerm === input.search.searchTerm &&
        s.searchType === input.search.searchType
    );
    if (isDuplicate) return existing;

    const timestamp = input.now ?? Date.now();
    const newSearch: RecentSearch = {
      ...input.search,
      id: timestamp.toString(),
      timestamp,
    };
    const key = `dealhawk-recent-searches-${input.retailer}`;
    const updated = [newSearch, ...existing].slice(0, input.limit ?? 3);
    input.storage.setItem(key, JSON.stringify(updated));
    return updated;
  } catch {
    return [];
  }
};

export const clearRecentSearches = (storage: Storage, retailer: string) => {
  try {
    storage.removeItem(`dealhawk-recent-searches-${retailer}`);
  } catch {}
};
